// Modal de transferência de assinatura — Story 3.7.
// DR-027/8.2 cessão de contrato. Cobranças passadas mantém cliente original.

import * as Dialog from '@radix-ui/react-dialog';
import { ArrowRightLeft, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { formatBR, parseLocalDate } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Textarea } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { useClientsSearch } from '../../hooks/use-clients';
import { useTransferSubscription } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import type { SubscriptionDetail } from '../../lib/subscriptions-api';

interface TransferModalProps {
  subscription: SubscriptionDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatBRL(amountString: string): string {
  const n = Number(amountString);
  if (!Number.isFinite(n)) return amountString;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function TransferModal({
  subscription,
  open,
  onOpenChange,
}: TransferModalProps): JSX.Element | null {
  const [query, setQuery] = useState('');
  const [toClientId, setToClientId] = useState<string>('');
  const [toClientName, setToClientName] = useState<string>('');
  const [reason, setReason] = useState('');
  const transferMutation = useTransferSubscription();

  // Busca clientes ativos pra autocomplete (debounce simples).
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const clientsQ = useClientsSearch(
    debouncedQuery.length >= 2 ? { q: debouncedQuery, status: 'ativo' } : {},
    10,
  );

  useEffect(() => {
    if (!open) {
      setQuery('');
      setToClientId('');
      setToClientName('');
      setReason('');
      transferMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Filtra cliente atual da lista (não dá pra transferir pra si mesmo)
  const candidates = useMemo(() => {
    const items = clientsQ.data?.items ?? [];
    return items.filter((c) => c.id !== subscription?.client.id);
  }, [clientsQ.data, subscription]);

  if (!subscription) return null;

  const reasonValid = reason.trim().length >= 5 && reason.trim().length <= 500;
  const canConfirm = Boolean(toClientId) && reasonValid && !transferMutation.isPending;

  async function handleConfirm(): Promise<void> {
    if (!canConfirm || !subscription) return;
    try {
      await transferMutation.mutateAsync({
        id: subscription.id,
        payload: { to_client_id: toClientId, reason: reason.trim() },
      });
      toast.success('Assinatura transferida.', {
        description: `${toClientName} agora é o titular. Cobranças passadas ficam com ${subscription.client.name}.`,
      });
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao transferir.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(580px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="flex items-center gap-2 text-[18px] font-semibold tracking-[-0.005em] text-ink">
                <ArrowRightLeft size={16} strokeWidth={1.8} className="text-warn" />
                Transferir assinatura
              </Dialog.Title>
              <p className="mt-1 text-[12px] text-muted">
                {subscription.plan?.name ?? '—'} ({formatBRL(subscription.price)}) · Atualmente de{' '}
                <strong>{subscription.client.name}</strong>
              </p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={15} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            className="flex flex-col gap-4"
          >
            <Field
              label="Cliente destino"
              required
              hint="Busque por nome ou CPF/CNPJ (apenas clientes ativos)"
            >
              {({ inputId }) => (
                <div className="relative">
                  <div className="relative">
                    <Search
                      size={14}
                      strokeWidth={1.8}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-3"
                    />
                    <input
                      id={inputId}
                      type="text"
                      value={toClientId ? toClientName : query}
                      onChange={(e) => {
                        // Selecionar cliente trava input com nome; editar reabre busca
                        if (toClientId) {
                          setToClientId('');
                          setToClientName('');
                        }
                        setQuery(e.target.value);
                      }}
                      placeholder="Digite ao menos 2 caracteres…"
                      className={cn(
                        'h-9 w-full rounded-[8px] border border-line bg-panel-2 pl-9 pr-3',
                        'text-[13px] text-ink placeholder:text-muted-3',
                        'focus:border-line-2 focus:outline-none focus:ring-1 focus:ring-line-2',
                      )}
                    />
                  </div>
                  {/* Dropdown de candidatos */}
                  {!toClientId && debouncedQuery.length >= 2 && (
                    <div
                      className={cn(
                        'absolute top-full z-10 mt-1 max-h-60 w-full overflow-y-auto',
                        'rounded-[8px] border border-line bg-panel shadow-lg',
                      )}
                    >
                      {clientsQ.isLoading && (
                        <div className="px-3 py-2 text-[12px] text-muted">Buscando…</div>
                      )}
                      {!clientsQ.isLoading && candidates.length === 0 && (
                        <div className="px-3 py-2 text-[12px] text-muted">
                          Nenhum cliente encontrado.
                        </div>
                      )}
                      {candidates.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setToClientId(c.id);
                            setToClientName(c.name);
                            setQuery('');
                          }}
                          className="flex w-full flex-col items-start gap-0.5 border-b border-line px-3 py-2 text-left last:border-0 hover:bg-panel-2"
                        >
                          <span className="text-[13px] font-medium text-ink">{c.name}</span>
                          {c.document && (
                            <span className="text-[11px] text-muted">{c.document}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Field>

            <Field
              label="Motivo"
              required
              hint="Min. 5 / max. 500 caracteres (auditoria)"
              error={
                reason.length > 0 && !reasonValid
                  ? reason.trim().length < 5
                    ? 'Mínimo 5 caracteres.'
                    : 'Máximo 500 caracteres.'
                  : undefined
              }
            >
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={3}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex: Empresa vendida pra novo CNPJ / cessão de contrato"
                />
              )}
            </Field>

            {/* Preview destrutivo */}
            {toClientId && reasonValid && (
              <div className="rounded-[10px] border border-warn/30 bg-warn/5 p-3 text-[12px] leading-[1.5] text-ink-2">
                <strong>Confirmar transferência:</strong>{' '}
                <strong>{subscription.plan?.name ?? '—'}</strong> sai de{' '}
                <strong>{subscription.client.name}</strong> e vai pra{' '}
                <strong>{toClientName}</strong>. Cobranças passadas ficam vinculadas a{' '}
                <strong>{subscription.client.name}</strong> (preserva NF/fiscal). Próxima cobrança{' '}
                {subscription.nextBillingDate
                  ? `em ${formatBR(parseLocalDate(subscription.nextBillingDate))} `
                  : ''}
                já vai pro novo cliente.
              </div>
            )}

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => onOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton type="submit" solid disabled={!canConfirm}>
                {transferMutation.isPending ? 'Transferindo…' : 'Confirmar transferência'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
